import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ASHEVILLE_CENTER, ALLOWED_RADIUS_KM, isWithinAllowedArea } from '../config/boundaries';
import { sanitizeText, sanitizeInstagramHandle, createInstagramURL, createMapsURL } from '../utils/sanitize';
import { SERVICE_OPTIONS } from './OnboardingModal';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom food truck icon
const truckIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3448/3448609.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
});

// User's own truck icon (different color)
const myTruckIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3448/3448609.png',
  iconSize: [50, 50],
  iconAnchor: [25, 50],
  popupAnchor: [0, -50],
  className: 'my-truck-icon',
});

// Closed truck icon (grayed out)
const closedTruckIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3448/3448609.png',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40],
  className: 'closed-truck-icon',
});

// User's own truck when closed
const myClosedTruckIcon = new L.Icon({
  iconUrl: 'https://cdn-icons-png.flaticon.com/512/3448/3448609.png',
  iconSize: [50, 50],
  iconAnchor: [25, 50],
  popupAnchor: [0, -50],
  className: 'my-truck-icon closed-truck-icon',
});

// Component to handle map clicks
function MapClickHandler({ isSettingLocation, onSetLocation }) {
  useMapEvents({
    click: (e) => {
      if (isSettingLocation) {
        const { lat, lng } = e.latlng;
        if (isWithinAllowedArea(lat, lng)) {
          onSetLocation(lat, lng);
        } else {
          alert('📍 This location is outside the allowed area.\n\nFood trucks can only pin locations within the Asheville area (15km radius from downtown).');
        }
      }
    },
  });
  return null;
}

// Component to center map on selected location
function MapCenterHandler({ selectedLocation }) {
  const map = useMap();
  
  useEffect(() => {
    if (selectedLocation) {
      map.setView([selectedLocation.latitude, selectedLocation.longitude], 15);
    }
  }, [selectedLocation, map]);
  
  return null;
}

function Map({ locations, selectedLocation, isSettingLocation, onSetLocation, onSelectLocation, user }) {
  // Default center: Asheville, NC
  const defaultCenter = [35.5951, -82.5515];
  const mapRef = useRef(null);

  return (
    <MapContainer
      center={defaultCenter}
      zoom={13}
      className="map"
      ref={mapRef}
      style={{ cursor: isSettingLocation ? 'crosshair' : 'grab' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      <MapClickHandler
        isSettingLocation={isSettingLocation}
        onSetLocation={onSetLocation}
      />
      
      <MapCenterHandler selectedLocation={selectedLocation} />

      {/* Allowed area boundary circle */}
      <Circle
        center={[ASHEVILLE_CENTER.lat, ASHEVILLE_CENTER.lng]}
        radius={ALLOWED_RADIUS_KM * 1000}
        pathOptions={{
          color: '#ff6b35',
          fillColor: '#ff6b35',
          fillOpacity: 0.05,
          weight: 2,
          dashArray: '10, 10'
        }}
      />

      {locations.map((location) => {
        const isOwner = user && location.user_id === user.id;
        const isOpen = location.is_open === 1 || location.is_open === true;
        
        // Get active services for this location
        const activeServices = location.services 
          ? SERVICE_OPTIONS.filter(s => location.services[s.key])
          : [];
        
        // Choose the appropriate icon based on ownership and status
        let markerIcon;
        if (isOwner) {
          markerIcon = isOpen ? myTruckIcon : myClosedTruckIcon;
        } else {
          markerIcon = isOpen ? truckIcon : closedTruckIcon;
        }
        
        return (
          <Marker
            key={location.id}
            position={[location.latitude, location.longitude]}
            icon={markerIcon}
            eventHandlers={{
              click: () => onSelectLocation(location),
            }}
          >
            <Popup>
              <div className="popup-content">
                <div className="popup-header">
                  <h3>{sanitizeText(location.truck_name || location.username)}</h3>
                  <span className={`status-badge ${isOpen ? 'status-open' : 'status-closed'}`}>
                    {isOpen ? '🟢 Open' : '🔴 Closed'}
                  </span>
                </div>
                {location.instagram && (
                  <p className="instagram-link">
                    <a 
                      href={createInstagramURL(location.instagram)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      📸 @{sanitizeInstagramHandle(location.instagram)}
                    </a>
                  </p>
                )}
                {location.truck_description && <p>{sanitizeText(location.truck_description)}</p>}
                {activeServices.length > 0 && (
                  <div className="popup-services">
                    {activeServices.map(service => (
                      <span key={service.key} className="service-badge" title={service.label}>
                        {service.icon}
                      </span>
                    ))}
                  </div>
                )}
                {location.address && <p><strong>📍</strong> {sanitizeText(location.address)}</p>}
                <p><small>Updated: {new Date(location.updated_at).toLocaleString()}</small></p>
                <a
                  href={createMapsURL(location.latitude, location.longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="directions-btn"
                >
                🗺️ Get Directions
              </a>
            </div>
          </Popup>
        </Marker>
        );
      })}
    </MapContainer>
  );
}

export default Map;
